import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItemButton,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  LinearProgress,
  Paper,
  Stack,
  Divider,
} from '@mui/material';
import {
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  OpenInNew as OpenInNewIcon,
  MenuBook as MenuBookIcon,
} from '@mui/icons-material';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

interface SourceMapEntry {
  source: string;
  access: string;
  update_frequency: string;
  difficulty: string;
  notes: string;
  source_url?: string;
}

interface ConsentType {
  id: number;
  code: string;
  name: string;
  description: string | null;
  legislation: string | null;
  category_id: number;
  category_name: string | null;
  default_cgb: { id: number; code: string; name: string } | null;
  source_map: SourceMapEntry | null;
}

interface Category {
  id: number;
  code: string;
  name: string;
  color: string;
}

// Colour coding for access method and integration difficulty chips
const ACCESS_COLOR: Record<string, string> = {
  API: '#16A34A',
  Portal: '#2563EB',
  'Portal/manual': '#D97706',
  Manual: '#6B7280',
};

const DIFFICULTY_COLOR: Record<string, string> = {
  Medium: '#16A34A',
  High: '#D97706',
  'Very high': '#DC2626',
};

const ConsentGuide: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [consentTypes, setConsentTypes] = useState<ConsentType[]>([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState('');
  const [accessFilter, setAccessFilter] = useState('');
  const [difficultyFilter, setDifficultyFilter] = useState('');

  const [selected, setSelected] = useState<ConsentType | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [catRes, typesRes] = await Promise.all([
          fetch(`${API_BASE}/consents/categories`),
          fetch(`${API_BASE}/consents/consent-types`),
        ]);
        setCategories(await catRes.json());
        setConsentTypes(await typesRes.json());
      } catch (err) {
        console.error('Failed to load consent guide data:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return consentTypes.filter((ct) => {
      if (q && !`${ct.code} ${ct.name}`.toLowerCase().includes(q)) return false;
      if (accessFilter && ct.source_map?.access !== accessFilter) return false;
      if (difficultyFilter && ct.source_map?.difficulty !== difficultyFilter) return false;
      return true;
    });
  }, [consentTypes, search, accessFilter, difficultyFilter]);

  const grouped = useMemo(() => {
    const byCategory: Record<number, ConsentType[]> = {};
    filtered.forEach((ct) => {
      byCategory[ct.category_id] = byCategory[ct.category_id] || [];
      byCategory[ct.category_id].push(ct);
    });
    return byCategory;
  }, [filtered]);

  if (loading) {
    return <LinearProgress />;
  }

  return (
    <Box sx={{ p: { xs: 0, sm: 1, md: 3 }, minWidth: 0 }}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 0.5 }}>
        <MenuBookIcon color="primary" fontSize="large" />
        <Typography variant="h4" sx={{ fontSize: { xs: '1.8rem', sm: '2.125rem' } }}>Consent Guide</Typography>
      </Stack>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 780 }}>
        Every consent type, its authoritative source, how it's accessed, how often it updates, and how hard it is to integrate.
      </Typography>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={5}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by code or name..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3.5}>
            <FormControl fullWidth size="small">
              <InputLabel>Access Method</InputLabel>
              <Select
                label="Access Method"
                value={accessFilter}
                onChange={(e) => setAccessFilter(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                {Object.keys(ACCESS_COLOR).map((a) => (
                  <MenuItem key={a} value={a}>{a}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3.5}>
            <FormControl fullWidth size="small">
              <InputLabel>Difficulty</InputLabel>
              <Select
                label="Difficulty"
                value={difficultyFilter}
                onChange={(e) => setDifficultyFilter(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                {Object.keys(DIFFICULTY_COLOR).map((d) => (
                  <MenuItem key={d} value={d}>{d}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {filtered.length} of {consentTypes.length} consent types
      </Typography>

      {/* Grouped by category */}
      {categories
        .filter((cat) => grouped[cat.id]?.length)
        .map((cat) => (
          <Accordion key={cat.id} defaultExpanded sx={{ mb: 1 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: cat.color }} />
                <Typography variant="subtitle1" fontWeight={600}>{cat.name}</Typography>
                <Chip size="small" label={grouped[cat.id].length} />
              </Stack>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              <List disablePadding>
                {grouped[cat.id]
                  .sort((a, b) => a.code.localeCompare(b.code))
                  .map((ct, idx) => (
                    <React.Fragment key={ct.id}>
                      {idx > 0 && <Divider component="li" />}
                      <ListItemButton onClick={() => setSelected(ct)} sx={{ py: 1.25, alignItems: { xs: 'flex-start', sm: 'center' }, flexWrap: { xs: 'wrap', sm: 'nowrap' }, gap: { xs: 0.75, sm: 1.5 } }}>
                        <ListItemText
                          primary={
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 0.25, sm: 1 }} alignItems={{ xs: 'flex-start', sm: 'center' }} sx={{ minWidth: 0 }}>
                              <Typography variant="body2" fontWeight={600} sx={{ minWidth: { sm: 70 } }}>
                                {ct.code}
                              </Typography>
                              <Typography variant="body2">{ct.name}</Typography>
                            </Stack>
                          }
                        />
                        {ct.source_map && (
                          <Stack direction={{ xs: 'row', sm: 'row' }} spacing={1} sx={{ width: { xs: '100%', sm: 'auto' }, justifyContent: { xs: 'flex-start', sm: 'flex-end' }, flexWrap: 'wrap' }}>
                            <Chip
                              size="small"
                              label={ct.source_map.access}
                              sx={{ backgroundColor: ACCESS_COLOR[ct.source_map.access], color: '#fff' }}
                            />
                            <Chip
                              size="small"
                              label={ct.source_map.difficulty}
                              sx={{ backgroundColor: DIFFICULTY_COLOR[ct.source_map.difficulty], color: '#fff' }}
                              variant="outlined"
                            />
                          </Stack>
                        )}
                      </ListItemButton>
                    </React.Fragment>
                  ))}
              </List>
            </AccordionDetails>
          </Accordion>
        ))}

      {/* Detail dialog */}
      <Dialog open={!!selected} onClose={() => setSelected(null)} maxWidth="sm" fullWidth>
        {selected && (
          <>
            <DialogTitle>
              <Typography variant="overline" color="text.secondary">{selected.code}</Typography>
              <Typography variant="h6">{selected.name}</Typography>
            </DialogTitle>
            <DialogContent dividers>
              <Stack spacing={2}>
                {selected.legislation && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">Legislation</Typography>
                    <Typography variant="body2">{selected.legislation}</Typography>
                  </Box>
                )}
                {selected.default_cgb && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">Consent Granting Body</Typography>
                    <Typography variant="body2">{selected.default_cgb.name}</Typography>
                  </Box>
                )}
                {selected.source_map ? (
                  <>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Authoritative Source</Typography>
                      <Typography variant="body2">{selected.source_map.source}</Typography>
                    </Box>
                    <Stack direction="row" spacing={1}>
                      <Chip
                        label={`Access: ${selected.source_map.access}`}
                        sx={{ backgroundColor: ACCESS_COLOR[selected.source_map.access], color: '#fff' }}
                      />
                      <Chip
                        label={`Difficulty: ${selected.source_map.difficulty}`}
                        sx={{ backgroundColor: DIFFICULTY_COLOR[selected.source_map.difficulty], color: '#fff' }}
                      />
                    </Stack>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Update Frequency</Typography>
                      <Typography variant="body2">{selected.source_map.update_frequency}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Notes</Typography>
                      <Typography variant="body2">{selected.source_map.notes}</Typography>
                    </Box>
                  </>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No source map entry recorded for this consent type yet.
                  </Typography>
                )}
              </Stack>
            </DialogContent>
            <DialogActions>
              {selected.source_map?.source_url && (
                <Button
                  href={selected.source_map.source_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  startIcon={<OpenInNewIcon />}
                >
                  View Source
                </Button>
              )}
              <Button onClick={() => setSelected(null)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default ConsentGuide;
